import React, {useState} from 'react';
import {curry} from 'ramda';
import wrap from 'wraptile';
import {useLocation} from 'react-router-dom';
import ISO6391 from 'iso-639-1';
import AppBar from '@material-ui/core/AppBar';
import Tab from '@material-ui/core/Tab';
import TabContext from '@material-ui/lab/TabContext';
import Tabs from '@material-ui/core/Tabs';
import TabPanel from '@material-ui/lab/TabPanel';
import Tooltip from '@material-ui/core/Tooltip';
import BorderColorIcon from '@material-ui/icons/BorderColor';
import {
    MetadataLocalized,
    TabLabelGetter,
    SetCurrentTab,
    HandleChangeEvent,
    MetadataLocalizedLanguage,
    KeywordItem,
    MetadataContentType,
    CategoryItem,
    SubCategory,
} from './ContentItemViewMetadataTypes';
import ContentItemViewMetadataLocalizedTab from './ContentItemViewMetadataLocalizedTab';
import {setClassNotValidTab} from './ContentItemViewMetadataTerritorial';
import {
    OnInputChangeEvent,
    SelectChangeEvent,
} from '../../QCWorkflow/QCManager/QCManagerTypes';

export interface MetadataUpdateHandlers {
    id: number;
    body: MetadataLocalizedLanguage[];
}

interface EditableValuesHandlers {
    titleEditable: string;
    onTitleChange: (v: string) => void;
    shortDescriptionEditable: string;
    longDescriptionEditable: string;
    onShortDescriptionChange: (v: string) => void;
    onLongDescriptionChange: (v: string) => void;
    categoryEditable: string;
    onCategoryChange: (v: string) => void;
    keywordsEditable: KeywordItem[];
    setEditableKeywords: (v: KeywordItem[]) => void;
    newKeyword: string;
    setNewKeyword: (v: string) => void;
}

interface ResetValuesHandlers {
    title: string;
    setEditableTitle: (v: string) => void;
    shortDescription: string;
    setEditableShortDescription: (v: string) => void;
    longDescription: string;
    setEditableLongDescription: (v: string) => void;
    categoryValue: string;
    subCategoryValue: string;
    setCategoryEditable: (v: string) => void;
    setSubCategoryEditable: (v: string) => void;
    keywords: KeywordItem[];
    setEditableKeywords: (v: KeywordItem[]) => void;
    newKeyword: string;
    setNewKeyword: (v: string) => void;
}

export interface QCMetadataLocalizedProps {
    localized: MetadataLocalized;
    updateLocalizedMetadata: (params: MetadataUpdateHandlers) => void;
    editable: boolean;
    setEditable: (v: boolean) => void;
    metadataContentType: MetadataContentType;
    categories: CategoryItem[] | null;
    jobDeliveryDate: string;
}

export const handleChange = curry(
    (setCurrentTab: SetCurrentTab, event: HandleChangeEvent, newValue: string) => {
        setCurrentTab(newValue);
    }
);

export const getLocalizedTabLabel = (key: string | undefined): string => {
    if (key) {
        const languageName = ISO6391.getName(key);
        return `${languageName} (${key})`;
    }

    return 'N/A';
};

export const mapToTab = curry(
    (getLocalizedTabLabel: TabLabelGetter, value: string, i: number) => {
        const label = getLocalizedTabLabel(value);

        return <Tab label={label} value={value} key={value} id={`localized-tab-${i}`} />;
    }
);

export const delayAndSetTab = wrap(
    (
        index: number,
        isValid: boolean,
        idKey: string,
        setClassNotValidTab: (val: boolean, tab: HTMLElement, head: HTMLElement) => void
    ) => {
        const tab = document.getElementById(`${idKey}-tab-${index}`);
        const header = document.getElementById(`${idKey}-header`);
        setClassNotValidTab(isValid, tab as HTMLElement, header as HTMLElement);
    }
);

export const mapToTabsPanel = curry(
    (
        localized,
        possibleCategories,
        editable,
        editableValuesHandlers,
        metadataContentType,
        key,
        index: number
    ) => {
        const {
            title,
            shortDescription,
            longDescription,
            categories,
            keywords,
            subCategories,
        } = localized[key];
        const {
            titleEditable,
            shortDescriptionEditable,
            longDescriptionEditable,
            onShortDescriptionChange,
            onLongDescriptionChange,
            onTitleChange,
            categoryEditable,
            subCategoryEditable,
            onCategoryChange,
            onSubCategoryChange,
            keywordsEditable,
            setEditableKeywords,
            newKeyword,
            setNewKeyword,
        } = editableValuesHandlers;

        const uniqueKeywords = getUniqueKeywords(keywords);
        const tabIsNotValid =
            !title ||
            !categories.length ||
            !shortDescription ||
            !subCategories.length ||
            !uniqueKeywords.length ||
            !longDescription;

        // let UI render components we need
        setTimeout(
            delayAndSetTab(index, !tabIsNotValid, 'localized', setClassNotValidTab),
            200
        );

        return (
            <TabPanel className="localized-tab" value={key} key={key}>
                <ContentItemViewMetadataLocalizedTab
                    possibleCategories={possibleCategories}
                    metadataContentType={metadataContentType}
                    title={title}
                    titleEditable={titleEditable}
                    categories={categories}
                    subCategories={subCategories}
                    keywords={uniqueKeywords}
                    editable={editable}
                    onTitleChange={onTitleChange}
                    shortDescription={shortDescription}
                    longDescription={longDescription}
                    shortDescriptionEditable={shortDescriptionEditable}
                    longDescriptionEditable={longDescriptionEditable}
                    onShortDescriptionChange={onShortDescriptionChange}
                    onLongDescriptionChange={onLongDescriptionChange}
                    categoryEditable={categoryEditable}
                    subCategoryEditable={subCategoryEditable}
                    onCategoryChange={onCategoryChange}
                    onSubCategoryChange={onSubCategoryChange}
                    keywordsEditable={keywordsEditable}
                    setEditableKeywords={setEditableKeywords}
                    newKeyword={newKeyword}
                    setNewKeyword={setNewKeyword}
                />
            </TabPanel>
        );
    }
);

export const getTabs = (getLocalizedTabLabel: TabLabelGetter, keys: string[]) => {
    const withLabel = mapToTab(getLocalizedTabLabel);
    return keys.map(withLabel);
};

export const getTabsPanel = (
    localized: MetadataLocalized,
    possibleCategories: CategoryItem[] | null,
    keys: string[],
    editable: boolean,
    editableValuesHandlers: EditableValuesHandlers,
    metadataContentType: MetadataContentType
) => {
    const withLocale = mapToTabsPanel(
        localized,
        possibleCategories,
        editable,
        editableValuesHandlers,
        metadataContentType
    );
    return keys.map(withLocale);
};

export const toggleEditButtons = (
    editable: boolean,
    toggleEditMode: () => void,
    onSave: () => void,
    disabled: boolean
) => {
    return editable ? (
        <div className="qc-metadata__buttons-wrapper">
            <div
                className="qc-metadata__button qc-metadata__button_cancel"
                role="button"
                onClick={toggleEditMode}
            >
                Cancel
            </div>
            <button
                type="button"
                className="qc-metadata__button qc-metadata__button_save"
                onClick={onSave}
                disabled={disabled}
            >
                <Tooltip
                    title={disabled ? 'Metadata issues must be fixed before saving' : ''}
                >
                    <span>Save</span>
                </Tooltip>
            </button>
        </div>
    ) : (
        <div className="qc-metadata__buttons-wrapper">
            <BorderColorIcon onClick={toggleEditMode} />
        </div>
    );
};

export const filterKeywords = curry((uniqueLabels: string[], keyword: KeywordItem) => {
    if (keyword.label.toLowerCase() === uniqueLabels[0]) {
        uniqueLabels.splice(0, 1);
        return true;
    }
    return false;
});

export const mapKeywords = ({label}: KeywordItem) => label.toLowerCase();

export const getUniqueKeywords = (keywords: KeywordItem[]) => {
    const uniqueLabels = Array.from(new Set(keywords.map(mapKeywords)));
    return keywords.filter(filterKeywords(uniqueLabels));
};

export const getCategoryObj = (
    possibleCategories: CategoryItem[] | null,
    categoryEditable: string
) => possibleCategories?.find((category) => category.label === categoryEditable);

export const getSubCategoryValue = (categoryObj: any, subCategoryEditable: string) =>
    categoryObj?.subCategories?.find(
        (subCategory: SubCategory) => subCategory.label === subCategoryEditable
    );

export const getIsDisabled = (
    titleEditable: string,
    shortDescriptionEditable: string,
    longDescriptionEditable: string,
    categoryEditable: string,
    subCategoryEditable: string,
    keywordsEditable: KeywordItem[],
    possibleCategories: CategoryItem[] | null
) => {
    const categoryObj = getCategoryObj(possibleCategories, categoryEditable);

    const subCategoryValue = getSubCategoryValue(categoryObj, subCategoryEditable);

    return (
        titleEditable?.trim().length === 0 ||
        shortDescriptionEditable?.trim().length === 0 ||
        longDescriptionEditable?.trim().length === 0 ||
        categoryEditable?.trim().length === 0 ||
        subCategoryEditable?.trim().length === 0 ||
        keywordsEditable.length === 0 ||
        !categoryObj ||
        !subCategoryValue
    );
};

export default function ContentItemViewMetadataLocalized(
    props: QCMetadataLocalizedProps
) {
    const {
        localized,
        updateLocalizedMetadata,
        editable,
        setEditable,
        metadataContentType,
        categories: possibleCategories,
        jobDeliveryDate,
    } = props;

    const [key] = Array.from(Object.values(localized));
    const {
        title,
        shortDescription,
        longDescription,
        keywords,
        categories,
        subCategories,
        id,
    } = key as MetadataLocalizedLanguage;
    const uniqueKeywords = getUniqueKeywords(keywords);
    const categoryValue = categories[0]?.label || '';
    const subCategoryValue = subCategories[0]?.label || '';

    const localizedTabsKeys: string[] = Object.keys(localized);
    const [currentTab, setCurrentTab] = useState<string>(localizedTabsKeys[0]);
    const [titleEditable, setEditableTitle] = useState<string>(title);
    const [shortDescriptionEditable, setEditableShortDescription] = useState<string>(
        shortDescription
    );
    const [longDescriptionEditable, setEditableLongDescription] = useState<string>(
        longDescription
    );
    const [categoryEditable, setEditableCategory] = useState<string>(categoryValue);
    const [subCategoryEditable, setSubCategoryEditable] = useState<string>(
        subCategoryValue
    );
    const [keywordsEditable, setEditableKeywords] = useState<KeywordItem[]>(
        uniqueKeywords
    );
    const [newKeyword, setNewKeyword] = useState<string>('');
    const {pathname} = useLocation();
    const jobId = Number(pathname.slice(pathname.lastIndexOf('/') + 1));
    const disabled = getIsDisabled(
        titleEditable,
        shortDescriptionEditable,
        longDescriptionEditable,
        categoryEditable,
        subCategoryEditable,
        keywordsEditable,
        possibleCategories
    );

    const toggleEditMode = handleToggleEditMode(setEditable, editable, {
        title,
        setEditableTitle,
        shortDescription,
        setEditableShortDescription,
        longDescription,
        setEditableLongDescription,
        categoryValue,
        subCategoryValue,
        setCategoryEditable: setEditableCategory,
        setSubCategoryEditable,
        keywords: uniqueKeywords,
        setEditableKeywords,
        newKeyword,
        setNewKeyword,
        keywordsEditable,
    });

    const onTitleChange = handleTitleChange(setEditableTitle);
    const onShortDescriptionChange = handleShortDescriptionChange(
        setEditableShortDescription
    );
    const onLongDescriptionChange = handleLongDescriptionChange(
        setEditableLongDescription
    );
    const onCategoryChange = handleCategoryChange(setEditableCategory);
    const onSubCategoryChange = handleSubCategoryChange(setSubCategoryEditable);
    const editableValuesHandlers = {
        titleEditable,
        shortDescriptionEditable,
        longDescriptionEditable,
        onShortDescriptionChange,
        onLongDescriptionChange,
        onTitleChange,
        categoryEditable,
        subCategoryEditable,
        onCategoryChange,
        onSubCategoryChange,
        keywordsEditable,
        setEditableKeywords,
        newKeyword,
        setNewKeyword,
    };

    const metadataUpdateParams = {
        id: jobId,
        body: [
            {
                id,
                title: titleEditable,
                shortDescription: shortDescriptionEditable,
                longDescription: longDescriptionEditable,
                category: {
                    label: categoryEditable,
                },
                subCategory: {
                    label: subCategoryEditable,
                },
                keywords: keywordsEditable,
            },
        ],
    };

    const onSave = onSaveHandler(
        updateLocalizedMetadata,
        metadataUpdateParams,
        toggleEditMode
    );

    const onChange = handleChange(setCurrentTab);
    const tabs = getTabs(getLocalizedTabLabel, localizedTabsKeys);
    const tabsPanel = getTabsPanel(
        localized,
        possibleCategories,
        localizedTabsKeys,
        editable,
        editableValuesHandlers,
        metadataContentType
    );

    return (
        <>
            <div className="qc-metadata__localized-header">
                <span id="localized-header">Localized metadata</span>
                {!jobDeliveryDate &&
                    toggleEditButtons(editable, toggleEditMode, onSave, disabled)}
            </div>
            <div>
                <TabContext value={currentTab}>
                    <AppBar position="static">
                        <Tabs
                            value={currentTab}
                            onChange={onChange}
                            variant="scrollable"
                            scrollButtons="auto"
                            aria-label="scrollable localized tabs"
                        >
                            {tabs}
                        </Tabs>
                    </AppBar>
                    {tabsPanel}
                </TabContext>
            </div>
        </>
    );
}

export const handleTitleChange = curry(
    (setEditableTitle: (v: string) => void, e: SelectChangeEvent) => {
        setEditableTitle(e.target.value);
    }
);

export const handleShortDescriptionChange = curry(
    (setEditableShortDescription: (v: string) => void, e: OnInputChangeEvent) => {
        setEditableShortDescription(e.target.value);
    }
);

export const handleLongDescriptionChange = curry(
    (setEditableLongDescription: (v: string) => void, e: OnInputChangeEvent) => {
        setEditableLongDescription(e.target.value);
    }
);

export const handleCategoryChange = curry(
    (setCategoryEditable: (v: string) => void, e: SelectChangeEvent) => {
        setCategoryEditable(e.target.value);
    }
);

export const handleSubCategoryChange = curry(
    (setSubCategoryEditable: (v: string) => void, e: SelectChangeEvent) => {
        setSubCategoryEditable(e.target.value);
    }
);

export const handleToggleEditMode = wrap(
    (
        setEditable: (v: boolean) => void,
        editable: boolean,
        resetHandlers: ResetValuesHandlers
    ) => {
        const {
            title,
            setEditableTitle,
            shortDescription,
            setEditableShortDescription,
            longDescription,
            setEditableLongDescription,
            categoryValue,
            subCategoryValue,
            setCategoryEditable,
            setSubCategoryEditable,
            keywords,
            setEditableKeywords,
            setNewKeyword,
        } = resetHandlers;
        setEditable(!editable);
        setEditableTitle(title);
        setEditableShortDescription(shortDescription);
        setEditableLongDescription(longDescription);
        setCategoryEditable(categoryValue);
        setSubCategoryEditable(subCategoryValue);
        setEditableKeywords(keywords);
        setNewKeyword('');
    }
);

export const onSaveHandler = wrap(
    (
        updateLocalizedMetadata: (params: MetadataUpdateHandlers) => void,
        metadataUpdateParams: MetadataUpdateHandlers,
        toggleEditMode: () => void
    ) => {
        updateLocalizedMetadata(metadataUpdateParams);
        toggleEditMode();
    }
);
